import { useState, useCallback } from 'react';
import { slugify, transliterate } from 'src/utils/slug';

/**
 * Хук для керування полем slug у формах.
 *
 * Забезпечує:
 * - Фільтрацію введення (лише `a-z`, `0-9`, `-`, `_`)
 * - Коректну вставку з буферу обміну з урахуванням виділення тексту
 * - Автогенерацію slug з назви через транслітерацію + slugify
 *
 * @returns {{
 *   slug: string,
 *   setSlug: Function,
 *   slugManuallyEdited: boolean,
 *   setSlugManuallyEdited: Function,
 *   handleSlugChange: Function,
 *   handleSlugPaste: Function,
 *   autoSlugFromName: (name: string) => void
 * }}
 *
 * @example
 * const { slug, handleSlugChange, handleSlugPaste, autoSlugFromName } = useSlugField();
 *
 * <input value={slug} onChange={handleSlugChange} onPaste={handleSlugPaste} />
 * <button onClick={() => autoSlugFromName(name)}>Генерувати</button>
 */
export const useSlugField = () => {
	const [slug, setSlug] = useState('');
	const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

	const handleSlugChange = useCallback((e) => {
		const filtered = e.target.value.toLowerCase().replace(/[^a-z0-9\-_]/g, '');
		setSlug(filtered);
		setSlugManuallyEdited(true);
	}, []);

	const handleSlugPaste = useCallback((e) => {
		e.preventDefault();
		const pasted = e.clipboardData.getData('text');
		const filtered = pasted.toLowerCase().replace(/[^a-z0-9\-_]/g, '');
		const start = e.target.selectionStart;
		const end = e.target.selectionEnd;
		setSlug(prev => {
			const s = start ?? prev.length;
			const en = end ?? prev.length;
			return (prev.slice(0, s) + filtered + prev.slice(en)).replace(/[^a-z0-9\-_]/g, '');
		});
		setSlugManuallyEdited(true);
	}, []);

	const autoSlugFromName = useCallback((name) => {
		setSlug(slugify(transliterate(name)));
		setSlugManuallyEdited(true);
	}, []);

	return {
		slug,
		setSlug,
		slugManuallyEdited,
		setSlugManuallyEdited,
		handleSlugChange,
		handleSlugPaste,
		autoSlugFromName,
	};
};
